import React from 'react';
import { Form, InputNumber } from 'antd';
import { ColumnType } from 'antd/es/table';
import moment, { Moment } from 'moment';
import { priceFormatter } from 'common/helpers/format.helper';
import { EUpdateStatus } from 'common/models/commonModel';
import { Check, Spinner } from 'common/svg';
import {
  ECutoffReason,
  EPurchaseOrderStatus,
  EPurchaseOrderStatusFilterKey,
  EPurchaseOrderStatusFilterLabel,
  EPurchaseOrderStatusLabel,
  IPurchaseOrder,
  IPurchaseOrderRecoBasisModel,
  IPurchaseOrderRecoModel,
  IPurchaseOrderRecoProduct,
  IPurchaseOrderRecoStoreModel,
  IRecoBasisDataSources,
} from 'entities/PurchaseOrder/PurchaseOrder.models';

// Asia/Manila is a fixed +08:00 offset with no DST — utcOffset is reliable without moment-timezone.
export const PHT_UTC_OFFSET = '+08:00';
// Leslie opens a new order window every evening at 17:00 PHT.
const ORDER_WINDOW_OPEN_HOUR = 17;
// deldate is unreliable around 17:00–17:01 PHT, so refetch a little after the window opens.
const ORDER_WINDOW_REFETCH_MINUTE = 2;

export const purchaseOrderNumberBuilder = (number: string) => {
  return `#SO${number}`;
};

export const toPhtMoment = (date?: string | null): Moment | null => {
  return date ? moment(date).utcOffset(PHT_UTC_OFFSET) : null;
};

// Nearest moment (strictly in the future) when a newly opened order window can be refetched.
export const getNextOrderWindowRefetchAt = (): Moment => {
  const now = moment().utcOffset(PHT_UTC_OFFSET);
  const refetchAt = now.clone().hour(ORDER_WINDOW_OPEN_HOUR).minute(ORDER_WINDOW_REFETCH_MINUTE).startOf('minute');

  if (!refetchAt.isAfter(now)) {
    refetchAt.add(1, 'day');
  }

  return refetchAt;
};

// The store can order only while the backend gives a delivery date and no closing reason.
export const isPurchaseOrderRecoOrderable = (reco: IPurchaseOrderRecoModel | null): boolean => {
  return !!reco?.deliverySchedule && !reco.cutoffReason;
};

// Why the store cannot order right now; null when ordering is open.
export const getPurchaseOrderClosedMessage = (reco: IPurchaseOrderRecoModel | null): string | null => {
  if (!reco || isPurchaseOrderRecoOrderable(reco)) {
    return null;
  }

  switch (reco.cutoffReason) {
    case ECutoffReason.WindowClosed: {
      const isBeforeOpening = moment().utcOffset(PHT_UTC_OFFSET).hour() < ORDER_WINDOW_OPEN_HOUR;

      return `Ordering opens ${isBeforeOpening ? 'today ' : ''}at 17:00 PHT.`;
    }
    case ECutoffReason.NotYourDeliveryDay:
      // Leslie's message to the franchisee — shown as is.
      return reco.cutoffRemarks || 'The open order window is outside your regular delivery days.';
    default:
      return 'No delivery date is currently available for this store. Please try again later.';
  }
};

export const mapPurchaseOrdersToKanbanDataSource = (data: IPurchaseOrder[]) => {
  const statuses = Object.values(EPurchaseOrderStatus);

  const map = new Map<string, IPurchaseOrder[]>();

  statuses.forEach((item) => {
    let key: string = item;

    if (item === EPurchaseOrderStatus.Delivered || item === EPurchaseOrderStatus.Canceled) {
      key = EPurchaseOrderStatusFilterKey.DeliveredCanceled;
    }

    if (!map.has(key)) {
      map.set(key, []);
    }

    const items = data.filter((i) => i.status === item);

    map.get(key)?.push(...items);
  });

  return Array.from(map).map(([key, items]) => {
    return {
      key,
      title:
        key === EPurchaseOrderStatusFilterKey.DeliveredCanceled
          ? EPurchaseOrderStatusFilterLabel[key as EPurchaseOrderStatusFilterKey]
          : EPurchaseOrderStatusLabel[key as EPurchaseOrderStatus],
      items,
    };
  });
};

// MBC-85: Track PO Kanban data source = RECO column (left) + 4 status columns.
// Items are a discriminated union; renderer narrows by 'sonum' in item.
export type TrackPoKanbanItem = IPurchaseOrder | IPurchaseOrderRecoStoreModel;

export const mapTrackPoKanbanDataSource = (orders: IPurchaseOrder[], recoStores: IPurchaseOrderRecoStoreModel[]) => {
  const statusColumns = mapPurchaseOrdersToKanbanDataSource(orders);
  return [
    {
      key: EPurchaseOrderStatusFilterKey.Reco,
      title: EPurchaseOrderStatusFilterLabel[EPurchaseOrderStatusFilterKey.Reco],
      items: recoStores as TrackPoKanbanItem[],
    },
    ...statusColumns.map((col) => ({ ...col, items: col.items as TrackPoKanbanItem[] })),
  ];
};

export const getGeneratePoConfig = (isMobile: boolean, disableOrderCount?: boolean) => {
  if (isMobile) {
    return [
      {
        key: 'category',
        dataIndex: 'category',
        title: (
          <div className="flex flex__column">
            <span>Category/</span>
            <span>Product Set</span>
          </div>
        ),
        render: (_, record) => {
          return (
            <div>
              <div className="ff-regular fs-xxxs color-gray-1">{record.category}</div>
              <span className="ff-regular fs-xxs">{record.shortName}</span>
            </div>
          );
        },
        width: '25%',
      },
      {
        key: 'recoCount',
        dataIndex: 'recoCount',
        title: 'Reco QTY',
        width: '20%',
        onHeaderCell: () => {
          return {
            style: {
              textWrap: 'nowrap',
              textAlign: 'center',
            },
          };
        },
        onCell: () => {
          return { style: { textAlign: 'center' } };
        },
        render: (_, record) => <span className="ff-regular fs-xxs color-gray-1">{record.recoCount}</span>,
      },
      {
        key: 'orderCount',
        dataIndex: 'orderCount',
        title: 'Order QTY',
        width: '20%',
        onHeaderCell: () => {
          return {
            style: {
              textWrap: 'nowrap',
              textAlign: 'center',
            },
          };
        },
        onCell: () => {
          return { style: { textAlign: 'center' } };
        },
        render: (_, record) => {
          return (
            <Form.Item name={[record?.key, 'orderCount']} style={{ margin: 0 }}>
              <InputNumber min={0} controls={false} disabled={disableOrderCount} />
            </Form.Item>
          );
        },
      },
      {
        key: 'totalAmount',
        dataIndex: 'totalAmount',
        title: (
          <div className="flex flex__column">
            <span>Unit Amount/</span>
            <span>Total Amount</span>
          </div>
        ),
        width: '35%',
        onHeaderCell: () => {
          return { style: { textAlign: 'end' } };
        },
        onCell: () => {
          return { style: { textAlign: 'end' } };
        },
        render: (_, record) => {
          return (
            <div>
              <div className="ff-regular fs-xxxs color-gray-1">{priceFormatter(record.price)}</div>
              <span className="ff-regular fs-xxs">{priceFormatter(record.orderCount * record.price)}</span>
            </div>
          );
        },
      },
    ] as ColumnType<IPurchaseOrderRecoProduct>[];
  }

  return [
    {
      key: 'category',
      dataIndex: 'category',
      title: 'Category',
      width: '12%',
      ellipsis: true,
    },
    {
      key: 'shortName',
      dataIndex: 'shortName',
      title: 'Product Set',
      width: '12%',
      ellipsis: true,
    },
    {
      key: 'price',
      dataIndex: 'price',
      title: 'Unit Amount',
      width: '16%',
      ellipsis: true,
      onHeaderCell: () => ({ style: { textAlign: 'end' } }),
      onCell: () => ({ style: { textAlign: 'end' } }),
      render: (text) => priceFormatter(text),
    },
    {
      key: 'recoCount',
      dataIndex: 'recoCount',
      title: 'Reco QTY',
      width: '12%',
      ellipsis: true,
      onHeaderCell: () => ({ style: { textAlign: 'end' } }),
      onCell: () => ({ style: { textAlign: 'end' } }),
    },
    {
      key: 'orderCount',
      dataIndex: 'orderCount',
      title: 'Order QTY',
      width: '12%',
      ellipsis: true,
      onHeaderCell: () => ({ style: { textAlign: 'end', color: '#3359ff' } }),
      onCell: () => ({ style: { textAlign: 'end', color: '#3359ff' } }),
      render: (_, record) => {
        return (
          <Form.Item name={[record?.key, 'orderCount']} style={{ margin: 0 }}>
            <InputNumber min={0} controls={false} disabled={disableOrderCount} />
          </Form.Item>
        );
      },
    },
    {
      key: 'totalAmount',
      title: 'Total Amount',
      width: '16%',
      ellipsis: true,
      render: (_, record) => priceFormatter(record.orderCount * record.price),
      onHeaderCell: () => ({ style: { textAlign: 'end' } }),
      onCell: () => ({ style: { textAlign: 'end' } }),
    },
    {
      key: 'status',
      dataIndex: 'status',
      render: (_, record) => {
        if (!record.status) {
          return null;
        }

        return (
          <div className="flex flex-align-center">
            {record.status === EUpdateStatus.Saved && <Check className="icon-purple" />}
            {record.status === EUpdateStatus.Saving && <Spinner className="icon-purple" />}
            <span className="ml-2 ff-regular fs-xxs color-purple">
              {record.status === EUpdateStatus.Saving ? 'Saving...' : 'Saved!'}
            </span>
          </div>
        );
      },
      width: '20%',
    },
  ] as ColumnType<IPurchaseOrderRecoProduct>[];
};

export const buildRecoBasisDataSources = (recoBasis: IPurchaseOrderRecoBasisModel | null): IRecoBasisDataSources => {
  const dataSources: IRecoBasisDataSources = {
    unitsSoldDataSource: [],
    trendDataSource: [],
    adjDemandDataSource: [],
    sellingPeriodDataSource: [],
    orderToDeliveryDataSource: [],
    safetyStockDataSource: [],
  };

  if (!recoBasis) {
    return dataSources;
  }

  const sellingPeriodStartMoment = toPhtMoment(recoBasis.sellingPeriod.startDate);
  const sellingPeriodEndMoment = toPhtMoment(recoBasis.sellingPeriod.endDate);
  const sellingPeriod =
    sellingPeriodStartMoment && sellingPeriodEndMoment
      ? `${sellingPeriodStartMoment.format('ddd')} to ${sellingPeriodEndMoment.format('ddd')}`.toLowerCase()
      : '-';

  dataSources.unitsSoldDataSource.push({
    key: 'unitsSold',
    criteria: 'Units Sold',
    sun: recoBasis.unitsSold.sun,
    mon: recoBasis.unitsSold.mon,
    tue: recoBasis.unitsSold.tue,
    wed: recoBasis.unitsSold.wed,
    thu: recoBasis.unitsSold.thu,
    fri: recoBasis.unitsSold.fri,
    sat: recoBasis.unitsSold.sat,
  });
  dataSources.trendDataSource.push({
    key: 'trend',
    last4Wk: recoBasis.trend.last4Wk,
    last8Wk: recoBasis.trend.last8Wk,
    trendFactor: recoBasis.trend.trendFactor,
  });
  dataSources.adjDemandDataSource.push({
    key: 'adjDemand',
    criteria: 'Adj. Demand',
    sun: recoBasis.adjDemand.sun,
    mon: recoBasis.adjDemand.mon,
    tue: recoBasis.adjDemand.tue,
    wed: recoBasis.adjDemand.wed,
    thu: recoBasis.adjDemand.thu,
    fri: recoBasis.adjDemand.fri,
    sat: recoBasis.adjDemand.sat,
  });
  dataSources.sellingPeriodDataSource.push({
    key: 'sellingPeriod',
    sellingPeriod,
    periodSum: recoBasis.periodSum,
    currentInventory: recoBasis.currentInv,
    preForecast: recoBasis.preForecast,
  });
  dataSources.orderToDeliveryDataSource.push({
    key: 'orderToDelivery',
    orderToDeliveryDemand: recoBasis.orderToDeliveryDemand,
  });
  dataSources.safetyStockDataSource.push({
    key: 'safetyStock',
    // Step 7: safety stock = 1/3 of the FIRST day of the selling period.
    safetyDay: recoBasis.sellingPeriod.firstDay,
    safetyStock: recoBasis.safetyStock,
    finalOrderQty: recoBasis.finalOrderQty,
    productSet: recoBasis.productSet,
    reco: recoBasis.reco,
  });

  return dataSources;
};
